import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Clock, Calendar, User, CheckCircle, Sparkles } from "lucide-react";
import { TopicLinks } from "@/components/blog/TopicLinks";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import SocialShare from "@/components/blog/SocialShare";
import skinHealthImage from "@/assets/blog/skin-health-bidet.jpg";

const SkinHealthBidet = () => {
  const publishDate = "2025-01-16";
  const modifiedDate = "2025-01-16";
  const readTime = "7 min read";
  const author = "Dr. Elena Rodriguez";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Skin Health and Bidets: Dermatologist-Recommended Hygiene Solutions",
    "image": skinHealthImage,
    "datePublished": publishDate,
    "dateModified": modifiedDate,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aureumvii.com/lovable-uploads/aureum-vii-trading-logo.png"
      }
    },
    "description": "Discover why dermatologists recommend bidet toilets for sensitive skin care. Learn how water cleansing protects skin health and reduces irritation."
  };

  return (
    <>
      <SEO
        title="Bidets and Skin Health Guide"
        description="Why water cleansing is kinder to skin than paper: less friction and irritation, plus the settings that suit sensitive skin best."
        keywords="skin health bidet, dermatologist bidet, sensitive skin toilet, skin irritation bidet, gentle cleansing toilet"
        url="https://aureumvii.com/blog/skin-health-bidet"
      />

      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>

      <Navigation />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <article className="relative">
          <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
            <img loading="lazy" decoding="async"
              src={skinHealthImage}
              alt="Clean bathroom for skin health and hygiene"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16">
              <div className="container mx-auto max-w-4xl">
                <Badge className="mb-4 bg-accent text-accent-foreground">
                  Skin Care
                </Badge>
                <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Skin Health and Bidets: Dermatologist-Recommended Hygiene Solutions
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="container mx-auto max-w-4xl px-4 py-12">
            <div className="prose prose-lg max-w-none">
              <p className="lead text-xl text-muted-foreground mb-8">
                The skin in intimate areas is among the most sensitive on the body. Dermatologists increasingly recommend bidet toilets as a gentle alternative to paper wiping, which can cause irritation, micro-tears, and exacerbate skin conditions.
              </p>

              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-primary mt-12 mb-6">
                Why Toilet Paper Can Harm Sensitive Skin
              </h2>
              <p className="text-muted-foreground mb-6">
                Even the softest toilet paper creates friction against delicate skin. This mechanical action can lead to micro-abrasions that may not be visible but can cause discomfort, itching, and increased vulnerability to infection. For those with existing skin conditions, this irritation compounds existing problems.
              </p>

              <Card className="my-8 border-accent/20">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Skin Conditions That Benefit from Bidets
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Eczema and psoriasis in intimate areas",
                      "Contact dermatitis from fragranced products",
                      "Pruritus ani (anal itching)",
                      "Fissures and sensitive skin post-surgery",
                      "Skin sensitivity from chemotherapy or medications"
                    ].map((condition, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{condition}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-primary mt-12 mb-6">
                The Dermatologist's Perspective
              </h2>
              <p className="text-muted-foreground mb-6">
                "Water cleansing is the gold standard for intimate hygiene," explains dermatology research. "It provides thorough cleansing without the friction that can damage the skin barrier. For patients with chronic skin conditions, switching to a bidet often provides significant relief."
              </p>

              <h3 className="font-playfair text-xl font-bold text-primary mt-8 mb-4">
                Preserving the Skin Barrier
              </h3>
              <p className="text-muted-foreground mb-6">
                The skin's acid mantle is a protective layer that guards against pathogens and irritants. Harsh wiping and chemical-laden wipes can disrupt this barrier. Water cleansing, especially at body temperature, respects the skin's natural protection while providing effective hygiene.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <Card className="border-accent/20">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">Temperature Control</h4>
                    <p className="text-muted-foreground text-sm">
                      Smart toilets allow precise temperature adjustment. Warm water soothes sensitive skin, while cooler water can help reduce inflammation during flare-ups.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-accent/20">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">Pressure Adjustment</h4>
                    <p className="text-muted-foreground text-sm">
                      Variable pressure settings ensure gentle cleansing even on the most sensitive skin. Low-pressure modes provide effective cleaning without irritation.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-primary mt-12 mb-6">
                Post-Procedure and Recovery Care
              </h2>
              <p className="text-muted-foreground mb-6">
                Bidet toilets are particularly valuable for patients recovering from procedures affecting the perianal area. Whether recovering from hemorrhoidectomy, childbirth, or other surgeries, gentle water cleansing provides hygiene without disturbing healing tissues.
              </p>

              <h3 className="font-playfair text-xl font-bold text-primary mt-8 mb-4">
                Warm Air Drying Benefits
              </h3>
              <p className="text-muted-foreground mb-6">
                Many smart toilets include warm air drying functions that eliminate the need for any paper contact. This feature is especially beneficial for those with extremely sensitive skin or active skin conditions where even patting dry with paper causes discomfort.
              </p>

              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-primary mt-12 mb-6">
                Recommended Products for Sensitive Skin
              </h2>
              <p className="text-muted-foreground mb-6">
                For optimal skin care, choose smart toilets with comprehensive hygiene features:
              </p>

              <div className="bg-secondary/30 p-6 rounded-lg my-8">
                <h4 className="font-semibold mb-4">Top Picks for Sensitive Skin:</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li><strong>LUXA Series:</strong> Premium adjustable pressure and temperature for customized comfort</li>
                  <li><strong>ARIA Series:</strong> Gentle oscillating spray with warm air drying</li>
                  <li><strong>LUNA M9:</strong> Multiple pressure settings with dedicated gentle mode</li>
                </ul>
              </div>

              {/* CTA Section */}
              <Card className="my-12 bg-gradient-to-r from-accent/10 to-primary/10 border-accent/20">
                <CardContent className="p-8 text-center">
                  <h3 className="font-playfair text-2xl font-bold text-primary mb-4">
                    Gentle Care for Your Skin
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Experience the difference that gentle water cleansing makes for sensitive skin. Explore our range of smart toilets designed with skin health in mind.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
                      <Link to="/smart-toilets">
                        Explore Products <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                      <Link to="/contact">Get Expert Advice</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <SocialShare 
              url="https://aureumvii.com/blog/skin-health-bidet"
              title="Skin Health and Bidets: Dermatologist-Recommended Hygiene Solutions"
            />
          </div>
        </article>
            <TopicLinks
              links={[
              { to: "/products/luna", label: "LUNA M9 wellness smart toilet" },
              { to: "/products/luxa", label: "LUXA flagship smart toilet" },
              { to: "/smart-toilets", label: "Browse the full smart toilet collection" },
              { to: "/blog/bidet-health-benefits", label: "Health benefits of bidet washing" },
            ]}
            />


        <RelatedArticles 
          currentSlug="skin-health-bidet"
        />
      </main>

      <Footer />
    </>
  );
};

export default SkinHealthBidet;
